/**
 * VRG-156 — the canonical tag-shop catalog (data): one product per form factor in
 * `FORMS` below, each with a selectable Capability ladder × Size × Quantity variant
 * grid. The count is deliberately not written here — it read "7 form-factor products"
 * for as long as `FORMS` has had 8 entries (Card was added later).
 *
 * REVERTS the VERA-246 form×capability (18-product) build back to the strategy
 * model in supply/tag-shop-catalog.md: Product = form; Capability is a customer-
 * selectable variant axis. Prices are computed by pricing.ts, so platform + Shopify
 * agree once the human re-seeds Shopify from this same model.
 *
 * ⚠️ This module is frontend/constants only — it does NOT write to Shopify.
 */
import type { TagFamily, ProductCategory } from '@repo/types'
import {
  type TagCapability,
  type TagFormFactor,
  type QuantityTier,
  QUANTITY_TIERS,
  SIDES,
  buildSku,
  casePrice,
  unitPrice,
  capabilityToTagFamily,
  type TagShape,
} from './pricing'
import { FORM_META, sizesFor, bestForForForm, bestForCategoriesForForm, shapesFor } from './affinity'
import { capabilityLeaves, LEAF_LABEL } from './capabilities'

// ─── Per-form metadata NOT covered by FORM_META (title/description/on-metal/…) ─

type FormInfo = {
  /** customer-facing product name (e.g. Solid → "Solid Tag"). */
  title: string
  descriptionHtml: string
  /** on_metal_available metafield — gates the On-Metal add-on (VRG-142). */
  onMetal: boolean
  /** Fabric forms — VERA-configurator durability grades. */
  durability?: string[]
  /** Fabric forms — VERA-configurator embroidery selection. */
  embroidery?: string[]
  /** Card — VERA-configurator construction (Plastic / Metal). */
  cardMaterial?: string[]
}

/**
 * ⚠️ THE WORD "NFC" DOES NOT APPEAR IN ANY `descriptionHtml`. Spencer, 2026-09-22:
 * "It shouldn't mention NFC at all. It should just describe the tag materials and
 * what applications it's good for."
 *
 * All eight descriptions previously opened on the acronym — "Flexible adhesive NFC
 * label…", "Rigid NFC disc…", "Woven NFC label…". That told a buyer the radio
 * standard and almost nothing about the object they were choosing between, which is
 * the wrong information at this point in the funnel: every product in this shop is
 * NFC, so naming it eight times distinguishes nothing. The site made the same call
 * for its own tag copy — `content/tag-forms.ts` blurbs never say NFC either.
 *
 * VOICE borrowed from the marketing site's `attach.why` field
 * (`content/industry-detail.ts`), which Spencer pointed to as the target: name the
 * material, name where it goes, name what that placement survives. Concrete nouns,
 * no jargon, no capability ladder.
 *
 * ⚠️ BUT NOT THAT FIELD'S *RULES*, and the difference is load-bearing. The site's
 * own docblock forbids asserting any mechanism, tolerance or adhesive spec — it
 * calls inventing one "a fabricated specification" — because it is marketing prose
 * for a reader who is not buying yet. THIS IS A SHOP. The reader is configuring a
 * purchase, and every spec below drives a real configurator control:
 *
 *   Sticker      ferrite add-on exists          → the On-Metal toggle
 *   Dial         ferrite is built in            → why there is NO On-Metal toggle
 *   Solid        adhesive ⇒ one-sided + on-metal → two coupled configurator options
 *   FabricPatch  woven vs embroidered, grades   → construction + durability axes
 *   FabricLabel  durability grade               → the durability axis
 *   Card         ID-1 85.6×54mm, plastic/metal  → the construction axis
 *
 * Stripping those to match the site's voice exactly would have produced prettier
 * copy that a buyer cannot choose from. Keep the facts; drop the acronym.
 *
 * ⚠️ THESE ARE SEED VALUES, NOT WHAT THE STOREFRONT RENDERS. As the Card note below
 * has said all along, the live shop reads `title`/`description` from Shopify. Editing
 * here changes the grid in mock mode immediately and production only after the
 * emitter runs and Shopify is re-seeded. Do not report this as "the shop copy is
 * fixed" until that has happened.
 */
const FORM_INFO: Record<TagFormFactor, FormInfo> = {
  Sticker: {
    title: 'Sticker Tag',
    descriptionHtml:
      '<p>A thin adhesive label for a flat interior surface — inside a lining, under a flap, between layers of leather — where it stays out of sight and out of reach of wear. Flexible enough to sit on a curve and thin enough to disappear under a finish. A ferrite-backed version is available as an add-on for metal.</p>',
    onMetal: true,
  },
  Dial: {
    title: 'Dial Tag',
    descriptionHtml:
      '<p>A ferrite-backed disc sized to mount behind a watch dial, in the one pocket of space a metal case leaves free. The ferrite is part of the construction rather than an option, because the tag sits directly against the movement and case. For watches and instruments with no exterior surface to give up.</p>',
    /* ⚠️ FALSE MEANS "NO ADD-ON TO SELL", NOT "NOT ON METAL". This gates the
       configurator's On-Metal toggle, and a Dial is already ferrite — offering
       it would ask the buyer to pay twice for what the part is. The ferrite
       itself is recorded by FERRITE_BY_CONSTRUCTION in pricing.ts, which is
       what the surcharge must be driven from. */
    onMetal: false,
  },
  Solid: {
    title: 'Solid Tag',
    // VRG-246 — reframed from "always on-metal ferrite disc" to "rigid disc, optionally
    // on-metal (only with adhesive)". Adhesive is a configurator add-on, not a variant axis.
    descriptionHtml:
      '<p>A rigid disc for metal and hard goods — the format that takes handling, cleaning and temperature without deforming. Add adhesive and it mounts flat and one-sided, which also unlocks the ferrite-backed option for metal. Leave it adhesive-free and it is a two-sided disc you can hang, seat in a recess, or fix yourself.</p>',
    onMetal: true, // available when adhesive is selected (see the configurator)
  },
  FabricPatch: {
    title: 'Fabric Patch',
    // VRG-246 — construction axis: woven (thin, Standard or Extra-Durable) OR embroidery
    // (thicker, always Extra-Durable). Chosen in the configurator; not a variant axis.
    descriptionHtml:
      '<p>A patch stitched down on all edges, sitting on the surface where it reads as visible branding rather than hidden hardware. Woven is thin and comes in Standard or Extra-Durable; embroidered is thicker and always Extra-Durable. For apparel, leather goods and soft goods that get worn, washed and expected to last.</p>',
    onMetal: false,
    durability: ['Standard', 'Extra-Durable'],
  },
  FabricLabel: {
    title: 'Fabric Label',
    // VRG-246 — woven only (no construction choice); keeps its Standard/Extra-Durable grade.
    descriptionHtml:
      '<p>A woven label sewn into a side seam, in the position a care or brand label normally occupies — part of the garment\'s construction rather than something attached to it. Discreet rather than surface-mounted, with two usable faces. Pick a durability grade in the configurator.</p>',
    onMetal: false,
    durability: ['Standard', 'Extra-Durable'],
  },
  Closure: {
    title: 'Closure Tag',
    descriptionHtml:
      '<p>A seal that bridges a lid, cap or seam, so opening the product breaks the loop and the break does not close again. Single-arm wraps a neck or rim; double-arm spans a lid. For bottles, prescription and caviar tins, cosmetics and cases — anywhere the first opening is the event that matters.</p>',
    onMetal: true,
  },
  ClosureLoop: {
    title: 'Closure Loop',
    /* ⚠️ THE DESCRIPTION NAMES NO DIMENSION, because none is specced — the
       part is not made yet. It describes what the object does and where it
       goes, which is all that is true of it. Do not add a cord length or a body
       diameter here to make it read like the others; see FORM_META's `LOOP`
       note. The form is unbuyable (COMING_SOON_FORMS), so this copy is the only
       thing a reader gets — an invented figure here would be the whole claim. */
    descriptionHtml:
      "<p>A round body whose loop clamps the product's own attachment — a bag handle, a luggage loop, a swing-ticket string — and closes on it. Removing the tag means breaking the loop, and the break does not close again. Where Closure seals an edge or a cap, Closure Loop clamps a loop shut.</p>",
    /* ⚠️ FALSE MEANS "NO ADD-ON TO SELL", NOT "NOT ON METAL" — the same
       distinction Dial draws above. This form hangs on a cord clear of the
       product's body, so it is never read against a metal surface and a ferrite
       build would be a surcharge for nothing. */
    onMetal: false,
  },
  Card: {
    // "Card", not "Card Tag" — a card IS the object, so the suffix was redundant in a way
    // "Sticker Tag" is not (a sticker could be any sticker). NOTE: this constant is the
    // SEED source; the shop renders `product.title` live from Shopify, so renaming here
    // alone changes nothing on the storefront until the Shopify product is renamed too.
    title: 'Card',
    descriptionHtml:
      '<p>A wallet-format card in the standard ID-1 size (85.6&times;54mm), in plastic or metal, usable on both faces. It is carried rather than attached, so it belongs to a person instead of to an object. For membership clubs, loyalty programs and access credentials.</p>',
    onMetal: false,
    cardMaterial: ['Plastic', 'Metal'],
  },
}

/** Ordered form list — drives TAG_CATALOG's iteration order. */
/**
 * Display order for the shop grid.
 *
 * ⚠️ DERIVED FROM FORM_META, NOT HAND-LISTED. This was a literal array, and
 * adding `Dial` to the `TagFormFactor` union on 2026-09-18 did NOT add it here
 * — so the form existed, priced, sized and SKU'd, and simply never appeared in
 * TAG_CATALOG or the shop. TypeScript could not catch it: `TagFormFactor[]`
 * accepts any subset, unlike the `Record<TagFormFactor, …>` maps that did fail
 * to compile and forced every other site to be updated.
 *
 * `ORDER` is the display sequence only. A form missing from it still appears,
 * at the end — a new form is then merely in the wrong place rather than
 * invisible, which is the failure mode worth designing for.
 */
const ORDER: TagFormFactor[] = [
  // ClosureLoop sits next to Closure: they are the same mechanism (a loop that does
  // not re-close) applied to an edge and to an attachment, and a buyer comparing
  // them should not have to cross the grid. It is last-but-one on purpose within
  // that pair — it cannot be ordered yet.
  'Sticker', 'Solid', 'FabricPatch', 'FabricLabel', 'Closure', 'ClosureLoop', 'Card', 'Dial',
]
const FORMS: TagFormFactor[] = (Object.keys(FORM_META) as TagFormFactor[]).sort((a, b) => {
  const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b)
  return (ia < 0 ? ORDER.length : ia) - (ib < 0 ? ORDER.length : ib)
})

// ─── Enriched shapes the shop/configurator reads ─────────────────────────────

export type CatalogVariant = {
  sku: string
  capability: TagCapability
  size: string
  quantity: QuantityTier
  /** per-unit @ this tier (list, before add-ons). */
  unit_price: number
  /** case total for this quantity tier. */
  price: number
  /** chip family derived from the variant's capability (internal — never shown to buyers).
   *  VERA-455: the registry's `TagFamily` value, not a display part name — this feeds
   *  `mocks/tagShopMock.ts`, whose variants the encode helpers read. */
  tag_family: TagFamily
}

export type CatalogProduct = {
  form: TagFormFactor
  handle: string
  title: string
  descriptionHtml: string
  /**
   * PRODUCT TYPE grain (ALL_PRODUCT_TYPES, 68). The SEARCH INDEX and the product page's
   * per-size narrowing. Deliberately NOT what the shop grid renders — at form level
   * this is a union across sizes and over-claims. See `best_for_product_categories`.
   */
  best_for_product_types: string[]
  /**
   * VRG-610 — PRODUCT CATEGORY grain (PRODUCT_CATEGORIES, 23). What the shop GRID
   * renders, because the form-level union is a true statement at this grain and not
   * at the one above. Derived from `best_for_product_types`, never hand-set; `other` is omitted.
   */
  best_for_product_categories: ProductCategory[]
  /** the capability LADDER this form offers (customer-selectable variant axis). */
  capabilities: TagCapability[]
  /** union of atomic leaf labels across the ladder (e.g. ['Connect','Verify','Tamper',…]),
   * for the Shop's atomic Capability filter + the card's unioned badges. */
  capabilityLeaves: string[]
  /** every size offered across the ladder (Size facet). */
  sizes: string[]
  sides: number
  on_metal_available: boolean
  /** Derived from the size tokens — see `shapesFor`. Never hand-set. */
  shapes: TagShape[]
  /** Fabric-form configurator grades / Card construction (carried; UI wiring optional). */
  durability?: string[]
  embroidery?: string[]
  cardMaterial?: string[]
  variants: CatalogVariant[]
}

/** Build one form product with a fully-priced Capability × Size × Quantity variant grid. */
function buildProduct(form: TagFormFactor): CatalogProduct {
  const meta = FORM_META[form]
  const info = FORM_INFO[form]

  const variants: CatalogVariant[] = []
  for (const capability of meta.validCaps) {
    for (const size of sizesFor(form, capability)) {
      for (const qty of QUANTITY_TIERS) {
        variants.push({
          sku: buildSku(form, capability, size, qty),
          capability,
          size,
          quantity: qty,
          unit_price: unitPrice(form, capability),
          price: casePrice(form, capability, qty),
          tag_family: capabilityToTagFamily(capability),
        })
      }
    }
  }

  // Union of leaf labels across the whole ladder (for the atomic Capability filter + badges).
  const leafSet = new Set<string>()
  for (const cap of meta.validCaps) for (const l of capabilityLeaves(cap)) leafSet.add(LEAF_LABEL[l])

  return {
    form,
    handle: `vera-tag-${form.toLowerCase()}`,
    title: info.title,
    descriptionHtml: info.descriptionHtml,
    best_for_product_types: bestForForForm(form),
    best_for_product_categories: bestForCategoriesForForm(form),
    capabilities: meta.validCaps,
    capabilityLeaves: [...leafSet],
    sizes: meta.sizes,
    sides: SIDES[form] ?? 1,
    on_metal_available: info.onMetal,
    shapes: shapesFor(form),
    ...(info.durability ? { durability: info.durability } : {}),
    ...(info.embroidery ? { embroidery: info.embroidery } : {}),
    ...(info.cardMaterial ? { cardMaterial: info.cardMaterial } : {}),
    variants,
  }
}

/** The full catalog: 7 form products with a priced Capability·Size·Quantity grid + derived tag_family. */
export const TAG_CATALOG: CatalogProduct[] = FORMS.map(buildProduct)
